import React, { useCallback, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import cx from 'classnames';

import { SiteConfigManager, useSportState } from '@atlas/core';
import { Icon } from '@atlas/ui';

import {
  SimpleSearchInput,
  SimpleSearchResult,
  useSearchInputState,
  useSimpleSearchState,
} from 'components/Search';
import DropdownCategories from 'components/SimpleFilters/DropdownCategories';
import DropdownMarketTypes from 'components/SimpleFilters/DropdownMarket';
import DropdownTime from 'components/SimpleFilters/DropdownTime';
import useIsPrematch from 'components/Sportsbook/useIsPrematch';
import { useSearch } from 'hooks/useSearch';

const SimpleFilters = () => {
  const { params } = useRouteMatch();

  const isPrematch = useIsPrematch();
  const { mainMarketTypes } = useSportState();

  const { isOpened, setIsOpened } = useSimpleSearchState();
  const { setFocused } = useSearchInputState();
  const { searchString } = useSearch();

  const onOpenSearchHandler = useCallback(() => {
    setIsOpened(true);
    setFocused(true);
  }, [setFocused, setIsOpened]);

  const onCloseSearchHandler = useCallback(() => {
    setIsOpened(false);
    setFocused(false);
  }, [setFocused, setIsOpened]);

  const filtersClassName = cx('filters', { 'search-opened': isOpened });
  const hasOnlyHiddenMarketTypes = React.useMemo(
    () => mainMarketTypes?.every(({ show }) => !show),
    [mainMarketTypes],
  );

  const isEnableSearchMobile = React.useMemo(
    () =>
      SiteConfigManager.getConfig('config.config.sportsbook.enableSearchMobile') &&
      !params.tournamentName,
    [params.tournamentName],
  );

  const isEnableTimeFilterMobile = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.enableTimeFilterMobile'),
    [],
  );

  useEffect(() => {
    if (params.tournamentName) {
      onCloseSearchHandler();
      return;
    }
    if (searchString && !isOpened) {
      onOpenSearchHandler();
    }
  }, [searchString, isOpened, onOpenSearchHandler, params.tournamentName, onCloseSearchHandler]);

  const displayFilters =
    isOpened ||
    !params.tournamentName ||
    (isEnableTimeFilterMobile && isPrematch && !params.tournamentName) ||
    !hasOnlyHiddenMarketTypes ||
    isEnableSearchMobile;

  if (!displayFilters) return null;

  return (
    <div className="simple-filters">
      <div className={filtersClassName}>
        {isOpened ? (
          <SimpleSearchInput />
        ) : (
          <>
            <div className="filters-container">
              {!params.tournamentName && <DropdownCategories />}
              {isEnableTimeFilterMobile && isPrematch && !params.tournamentName && <DropdownTime />}
              {!hasOnlyHiddenMarketTypes && <DropdownMarketTypes />}
            </div>
            {isEnableSearchMobile && (
              <button className="icon-button" onClick={onOpenSearchHandler} type="button">
                <div className="icon-container">
                  <Icon className="icon-search" name="search" />
                </div>
              </button>
            )}
          </>
        )}
      </div>
      {params.tournamentName ? null : <SimpleSearchResult />}
    </div>
  );
};

export default SimpleFilters;
